<template>
  <div class="home-kanban">
    <div class="home-kanban__headers">
      <button
        v-for="col in columns"
        :key="col.id"
        type="button"
        class="home-kanban__header"
        :class="[
          `home-kanban__header--${col.tone}`,
          { 'home-kanban__header--expanded': expanded[col.id] },
        ]"
        @click="toggleColumn(col.id)"
      >
        <span class="home-kanban__header-title">
          {{ col.title || t(col.titleKey) }}
          <span class="home-kanban__header-count">{{ counts[col.id] ?? 0 }}</span>
        </span>
      </button>
    </div>

    <div
      v-show="expandedCount > 0"
      ref="expandedEl"
      class="home-kanban__expanded"
      :data-expanded="expandedCount"
    >
      <HomeKanbanPanel
        v-for="col in columns"
        :key="col.id"
        :column="col"
        :visible="expanded[col.id]"
        :search="search"
        :bu="bu"
        :selected-id="selectedId"
        :expanded-count="expandedCount"
        :reset-token="resetToken"
        :refresh-token="refreshToken"
        @select="emit('select', $event)"
        @total="onTotal(col.id, $event)"
        @chips-el="onChipsEl"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { HOME_KANBAN_COLUMNS } from './columns'
import HomeKanbanPanel from './HomeKanbanPanel.vue'

const props = defineProps({
  search: { type: String, default: '' },
  bu: { type: String, default: 'all' },
  selectedId: { type: Number, default: null },
  /** Инкремент снаружи → развернуть все + сбросить чипы */
  resetToken: { type: Number, default: 0 },
  /** Инкремент → перезагрузить данные колонок (без сброса чипов/раскрытия) */
  refreshToken: { type: Number, default: 0 },
  /** true после сброса: все свернуты (пустая страница) */
  collapseOnReset: { type: Boolean, default: true },
})

const emit = defineEmits(['select', 'total'])

const { t } = useI18n()
const columns = HOME_KANBAN_COLUMNS
const expandedEl = ref(null)
const chipEls = reactive({})

/** Стартовое состояние макета — все колонки развёрнуты */
const expanded = reactive(Object.fromEntries(columns.map(c => [c.id, true])))
const counts = reactive(Object.fromEntries(columns.map(c => [c.id, null])))

const expandedCount = computed(() => columns.filter(c => expanded[c.id]).length)

watch(
  () => props.resetToken,
  (token, prev) => {
    if (!prev && token === 0) return
    if (props.collapseOnReset) {
      columns.forEach(c => {
        expanded[c.id] = false
      })
    } else {
      columns.forEach(c => {
        expanded[c.id] = true
      })
    }
    nextTick(syncChipHeights)
  }
)

function toggleColumn(id) {
  expanded[id] = !expanded[id]
  nextTick(syncChipHeights)
}

function onTotal(id, total) {
  counts[id] = total
  emit('total', { key: id, total })
}

function onChipsEl({ id, el }) {
  chipEls[id] = el
  nextTick(syncChipHeights)
}

function syncChipHeights() {
  const panels = columns
    .filter(c => expanded[c.id])
    .map(c => chipEls[c.id])
    .filter(Boolean)
  panels.forEach(el => {
    el.style.minHeight = ''
  })
  if (panels.length < 2) return
  const max = Math.max(...panels.map(el => el.offsetHeight))
  panels.forEach(el => {
    el.style.minHeight = `${max}px`
  })
}

defineExpose({
  collapseAll() {
    columns.forEach(c => {
      expanded[c.id] = false
    })
  },
  expandAll() {
    columns.forEach(c => {
      expanded[c.id] = true
    })
    nextTick(syncChipHeights)
  },
})
</script>

<i18n>
{
  "ru": {
    "kanban": {
      "preAccess": "Pre-access brands",
      "access": "Access brands",
      "postAccess": "Post-access brands"
    }
  },
  "en": {
    "kanban": {
      "preAccess": "Pre-access brands",
      "access": "Access brands",
      "postAccess": "Post-access brands"
    }
  }
}
</i18n>
